import { ApiError } from '@/common/types/errors';
import camelcaseKeys from 'camelcase-keys';

export const API_URL = import.meta.env.VITE_API_URL;

type CacheOptions = {
  key: string;
  ttlMinutes: number;
};

export async function backendFetch<T>(
  path: string,
  requestOptions: RequestInit = {}
): Promise<T> {
  return performRequest<T>(path, requestOptions);
}

export async function backendFetchCached<T>(
  path: string,
  cacheOptions: CacheOptions,
  requestOptions: RequestInit = {}
): Promise<T> {
  validateCacheOptions(cacheOptions);

  const cachedData = fetchFromCache<T>(cacheOptions);
  if (cachedData) {
    return cachedData;
  }

  const result = await performRequest<T>(path, requestOptions);
  setCache(cacheOptions, result);
  return result;
}

export async function backendPost<TResponse = void>(
  path: string,
  body: BodyInit,
  requestOptions: RequestInit = {}
): Promise<TResponse> {
  return writeRequest(path, body, 'POST', requestOptions);
}

export async function backendPut<TResponse = void>(
  path: string,
  body: BodyInit,
  requestOptions: RequestInit = {}
): Promise<TResponse> {
  return writeRequest(path, body, 'PUT', requestOptions);
}

export async function backendPatch<TResponse = void>(
  path: string,
  body: BodyInit,
  requestOptions: RequestInit = {}
): Promise<TResponse> {
  return writeRequest(path, body, 'PATCH', requestOptions);
}

export async function backendDelete<TResponse = void>(
  path: string,
  requestOptions: RequestInit = {}
): Promise<TResponse> {
  return performRequest<TResponse>(path, {
    method: 'DELETE',
    ...requestOptions,
  });
}

// Internal write helper
async function writeRequest<TResponse = void>(
  path: string,
  body: BodyInit,
  method: 'POST' | 'PUT' | 'PATCH',
  requestOptions: RequestInit = {}
): Promise<TResponse> {
  return performRequest<TResponse>(path, {
    method: method,
    ...requestOptions,
    body: body,
  });
}

async function performRequest<T>(
  path: string,
  requestOptions: RequestInit
): Promise<T> {
  const url = `${API_URL}${path}`;
  const response = await fetch(url, requestOptions);

  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new ApiError(response.status, response.statusText, body);
  }

  const contentType = response.headers.get('Content-Type') || '';
  if (contentType.includes('application/json')) {
    const json = await response.json();
    return camelcaseKeys(json, { deep: true }) as T;
  }

  return null as T;
}

function fetchFromCache<T>(cacheOptions: CacheOptions): T | null {
  const cached = localStorage.getItem(cacheOptions.key);
  if (cached) {
    const { timestamp, data } = JSON.parse(cached);
    if (Date.now() - timestamp < cacheOptions.ttlMinutes * 60 * 1000) {
      return data as T;
    }
  }

  return null;
}

function setCache<T>(cacheOptions: CacheOptions, data: T): void {
  localStorage.setItem(
    cacheOptions.key,
    JSON.stringify({ timestamp: Date.now(), data })
  );
}

function validateCacheOptions(cacheOptions: CacheOptions) {
  if (!cacheOptions.key || !cacheOptions.ttlMinutes) {
    throw new Error(
      'Invalid cacheOptions: must include key (string) and ttlMinutes (number)'
    );
  }
}

export function invalidateCache(cacheKey: string): void {
  localStorage.removeItem(cacheKey);
}
